import React, { useMemo } from "react";

import { faDownload } from "@fortawesome/pro-solid-svg-icons/faDownload";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ColumnType } from "antd/es/table";
import Tag from "antd/es/tag";
import Tooltip from "antd/es/tooltip";
import { IntlShape } from "react-intl";

import ImportMetadata from "@mapmycustomers/shared/types/imports/ImportMetadata";
import ImportPayloadMapping from "@mapmycustomers/shared/types/imports/ImportPayloadMapping";
import { Button } from "@mapmycustomers/ui";

import TextWithInfo from "@app/component/typography/TextWithInfo";
import { downloadFile } from "@app/store/files/actions";
import { formatRawDate, formatRawDateRelative } from "@app/util/formatters";
import { getEntityTypeDisplayName } from "@app/util/ui";

import ImportHistoryRow from "../type/ImportHistoryRow";
import getSkipFileName from "../util/getSkipFileName";

import ImportStatus from "./ImportStatus";

const useHistoryTableConfig = (
  intl: IntlShape,
  onDownloadFile: typeof downloadFile
): ColumnType<ImportHistoryRow>[] =>
  useMemo(
    () => [
      {
        dataIndex: "mapping",
        key: "entityType",
        render: (mapping: ImportHistoryRow["mapping"]) => (
          <Tag>
            {getEntityTypeDisplayName(intl, mapping.type, { lowercase: false, plural: true })}
          </Tag>
        ),
        title: intl.formatMessage({
          id: "settings.importHistory.column.recordType",
          defaultMessage: "Record type",
          description: "Title of Record type column on imports history",
        }),
      },
      {
        dataIndex: "createdAt",
        key: "createdAt",
        render: (createdAt: string) => (
          <Tooltip title={formatRawDate(createdAt, "p", "-")}>
            {formatRawDateRelative(createdAt, Date.now(), "-", "PPP")}
          </Tooltip>
        ),
        title: intl.formatMessage({
          id: "settings.importHistory.column.date",
          defaultMessage: "Date",
          description: "Title of Date column on imports history",
        }),
      },
      {
        dataIndex: "importConfigId",
        key: "importConfigId",
        render: (importConfigId: null | number) => (
          <Tag>
            {importConfigId
              ? intl.formatMessage({
                  id: "settings.importHistory.column.importType.sftp",
                  defaultMessage: "SFTP",
                  description: "SFTP type of Import type column on imports history",
                })
              : intl.formatMessage({
                  id: "settings.importHistory.column.importType.manual",
                  defaultMessage: "Manual",
                  description: "Manual type of Import type column on imports history",
                })}
          </Tag>
        ),
        title: intl.formatMessage({
          id: "settings.importHistory.column.importType",
          defaultMessage: "Import type",
          description: "Title of Import type column on imports history",
        }),
      },
      {
        dataIndex: "mapping",
        key: "appliedImportConfig",
        render: ({ appliedImportConfig }: ImportPayloadMapping) => appliedImportConfig?.name ?? "",
        title: intl.formatMessage({
          id: "settings.importHistory.column.savedMatching",
          defaultMessage: "Saved Matching",
          description: "Title of Saved Matching column on imports history",
        }),
      },
      {
        dataIndex: "metadata",
        key: "createdCount",
        render: (metadata: ImportMetadata) => metadata.createdCount,
        title: intl.formatMessage({
          id: "settings.importHistory.column.recordsCreated",
          defaultMessage: "Records created",
          description: "Title of Records created column on imports history",
        }),
      },
      {
        dataIndex: "metadata",
        key: "updatedCount",
        render: (metadata: ImportMetadata) => metadata.updatedCount,
        title: intl.formatMessage({
          id: "settings.importHistory.column.recordsUpdated",
          defaultMessage: "Records updated",
          description: "Title of Records updated column on imports history",
        }),
      },
      {
        dataIndex: "warningsCount",
        key: "warningsCount",
        title: intl.formatMessage({
          id: "settings.importHistory.column.warnings",
          defaultMessage: "Warnings",
          description: "Title of Warnings column on imports history",
        }),
      },
      {
        dataIndex: "errorsCount",
        key: "errorsCount",
        title: intl.formatMessage({
          id: "settings.importHistory.column.errors",
          defaultMessage: "Errors",
          description: "Title of Errors column on imports history",
        }),
      },
      {
        dataIndex: "status",
        key: "status",
        render: (status: ImportHistoryRow["status"], importHistoryRow: ImportHistoryRow) => (
          <ImportStatus
            fileId={importHistoryRow.skipFileId}
            hasSkipFile={!!importHistoryRow.skipFileId}
            importHistoryRow={importHistoryRow}
            metadata={importHistoryRow.metadata}
            onDownloadFile={onDownloadFile}
            status={status}
          />
        ),
        title: intl.formatMessage({
          id: "settings.importHistory.column.status",
          defaultMessage: "Status",
          description: "Title of Status column on imports history",
        }),
      },
      {
        dataIndex: "skipFileId",
        key: "skipFileId",
        render: (fileId: ImportHistoryRow["skipFileId"], importHistoryRow: ImportHistoryRow) =>
          fileId && (
            <Button
              icon={<FontAwesomeIcon icon={faDownload} />}
              iconOnly
              onClick={() =>
                onDownloadFile({
                  fileId,
                  fileName: getSkipFileName(intl, importHistoryRow),
                })
              }
              type="link"
            />
          ),
        title: (
          <TextWithInfo
            info={intl.formatMessage({
              id: "settings.importHistory.column.skipFile.tooltip",
              defaultMessage:
                "If any columns in your file were skipped due to errors, you can download a Skip File which highlights those skipped columns. Then, make any necessary changes and import again.",
              description: "Tooltip of Skip file column on imports history",
            })}
          >
            {intl.formatMessage({
              id: "settings.importHistory.column.skipFile",
              defaultMessage: "Skip File",
              description: "Title of Skip file column on imports history",
            })}
          </TextWithInfo>
        ),
      },
    ],
    [intl, onDownloadFile]
  );

export default useHistoryTableConfig;
